import {AutoLinkNode, LinkNode} from '@lexical/link';
import {useLexicalComposerContext} from '@lexical/react/LexicalComposerContext';
import {mergeRegister} from '@lexical/utils';
import {loggedInEmployeeAtom} from 'atoms/employee';
import {ExcludeWordcountNode} from 'components/DSYS/editor/nodes/ExcludeWordcountNode';
import {HighlightNode} from 'components/DSYS/editor/nodes/HighlightNode';
import {PlaceholderNode} from 'components/DSYS/editor/nodes/PlaceholderNode';
import {$isRedactionNode, RedactionNode} from 'components/DSYS/editor/nodes/RedactionNode';
import {ThreadAwareTextNode} from 'components/DSYS/editor/nodes/ThreadAwareTextNode';
import {useAtomValue} from 'jotai';
import {
  $getNodeByKey,
  $hasUpdateTag,
  $nodesOfType,
  COLLABORATION_TAG,
  type EditorState,
  HISTORY_MERGE_TAG,
  type LexicalNode,
  SKIP_COLLAB_TAG,
  TextNode,
} from 'lexical';
import {AISuggestionMarkNode} from 'plugins/aiRules/AISuggestionMarkNode';
import {useCallback, useEffect, useRef} from 'react';

const getRedactionKeysNeedingAccessRender = (candidateKeys: Iterable<string>): string[] => {
  const keysToRender: string[] = [];

  for (const key of candidateKeys) {
    const node = $getNodeByKey(key);
    if ($isRedactionNode(node) && node.needsAccessRender()) {
      keysToRender.push(key);
    }
  }

  return keysToRender;
};

const applyToRedactionKeys = (keys: Iterable<string>, callback: (node: RedactionNode) => void) => {
  for (const key of keys) {
    const node = $getNodeByKey(key);
    if ($isRedactionNode(node)) {
      callback(node);
    }
  }
};

const findNearestRedactionAncestor = (node: LexicalNode | null): RedactionNode | null => {
  let currentNode = node;

  while (currentNode) {
    if ($isRedactionNode(currentNode)) {
      return currentNode;
    }
    currentNode = currentNode.getParent();
  }

  return null;
};

const markContainingRedactionDirty = (node: LexicalNode | null) => {
  if ($hasUpdateTag(COLLABORATION_TAG)) {
    return;
  }

  const redactionNode = findNearestRedactionAncestor(node);
  if (redactionNode) {
    redactionNode.markDirty();
  }
};

export const RedactionAccessPlugin = () => {
  const [editor] = useLexicalComposerContext();
  const emp = useAtomValue(loggedInEmployeeAtom);
  const trackedRedactionKeysRef = useRef<Set<string>>(new Set());
  const pendingMutationRedactionKeysRef = useRef<Set<string>>(new Set());

  const normalizeAccessLocally = useCallback(
    (candidateKeys?: Iterable<string>, sourceEditorState?: EditorState) => {
      const keysToRender = (sourceEditorState ?? editor.getEditorState()).read(() =>
        getRedactionKeysNeedingAccessRender(candidateKeys ?? trackedRedactionKeysRef.current)
      );

      if (keysToRender.length === 0) {
        return;
      }

      editor.update(
        () => {
          applyToRedactionKeys(keysToRender, (node) => {
            node.renderChildrenForAccess();
          });
        },
        {tag: [SKIP_COLLAB_TAG, HISTORY_MERGE_TAG]}
      );
    },
    [editor]
  );

  const collectAffectedRedactionKeys = useCallback(
    (
      prevEditorState: EditorState,
      editorState: EditorState,
      dirtyLeaves: Set<string>,
      dirtyElements: Map<string, boolean>
    ): string[] => {
      const dirtyNodeKeys = new Set<string>([...dirtyLeaves, ...dirtyElements.keys()]);
      if (dirtyNodeKeys.size === 0) {
        return [];
      }

      const affectedRedactionKeys = new Set<string>();
      const collectFromState = (state: EditorState) => {
        state.read(() => {
          dirtyNodeKeys.forEach((key) => {
            const redactionNode = findNearestRedactionAncestor($getNodeByKey(key));
            if (redactionNode) {
              affectedRedactionKeys.add(redactionNode.getKey());
            }
          });
        });
      };

      collectFromState(editorState);
      collectFromState(prevEditorState);

      return [...affectedRedactionKeys];
    },
    []
  );

  useEffect(() => {
    const unregister = mergeRegister(
      editor.hasNode(TextNode)
        ? editor.registerNodeTransform(TextNode, (node) => {
            markContainingRedactionDirty(node);
          })
        : () => {},
      editor.hasNode(ThreadAwareTextNode)
        ? editor.registerNodeTransform(ThreadAwareTextNode, (node) => {
            markContainingRedactionDirty(node);
          })
        : () => {},
      editor.hasNode(LinkNode)
        ? editor.registerNodeTransform(LinkNode, (node) => {
            markContainingRedactionDirty(node);
          })
        : () => {},
      editor.hasNode(AutoLinkNode)
        ? editor.registerNodeTransform(AutoLinkNode, (node) => {
            markContainingRedactionDirty(node);
          })
        : () => {},
      editor.hasNode(HighlightNode)
        ? editor.registerNodeTransform(HighlightNode, (node) => {
            markContainingRedactionDirty(node);
          })
        : () => {},
      editor.hasNode(PlaceholderNode)
        ? editor.registerNodeTransform(PlaceholderNode, (node) => {
            markContainingRedactionDirty(node);
          })
        : () => {},
      editor.hasNode(ExcludeWordcountNode)
        ? editor.registerNodeTransform(ExcludeWordcountNode, (node) => {
            markContainingRedactionDirty(node);
          })
        : () => {},
      editor.hasNode(AISuggestionMarkNode)
        ? editor.registerNodeTransform(AISuggestionMarkNode, (node) => {
            markContainingRedactionDirty(node);
          })
        : () => {},
      editor.registerNodeTransform(RedactionNode, (node) => {
        if ($hasUpdateTag(COLLABORATION_TAG)) {
          node.renderChildrenForAccess();
          return;
        }

        node.syncStoredContentFromVisibleChildren();
      }),
      editor.registerMutationListener(
        RedactionNode,
        (nodes) => {
          nodes.forEach((mutation, key) => {
            if (mutation === 'destroyed') {
              trackedRedactionKeysRef.current.delete(key);
              pendingMutationRedactionKeysRef.current.delete(key);
              return;
            }

            trackedRedactionKeysRef.current.add(key);
            pendingMutationRedactionKeysRef.current.add(key);
          });
        },
        {skipInitialization: false}
      ),
      editor.registerUpdateListener(
        ({editorState, prevEditorState, dirtyLeaves, dirtyElements, tags}) => {
          if (tags.has(SKIP_COLLAB_TAG)) {
            return;
          }

          if (tags.has(COLLABORATION_TAG)) {
            normalizeAccessLocally(
              editorState.read(
                // eslint-disable-next-line deprecation/deprecation -- We need a full snapshot of live redaction nodes after a collab sync pass.
                () => $nodesOfType(RedactionNode).map((node) => node.getKey())
              ),
              editorState
            );
            pendingMutationRedactionKeysRef.current.clear();
            return;
          }

          const affectedRedactionKeys = new Set(
            collectAffectedRedactionKeys(prevEditorState, editorState, dirtyLeaves, dirtyElements)
          );
          pendingMutationRedactionKeysRef.current.forEach((key) => {
            affectedRedactionKeys.add(key);
          });
          pendingMutationRedactionKeysRef.current.clear();

          if (affectedRedactionKeys.size === 0) {
            return;
          }
          normalizeAccessLocally(affectedRedactionKeys, editorState);
        }
      )
    );

    return unregister;
  }, [collectAffectedRedactionKeys, editor, normalizeAccessLocally]);

  // Re-render when access context changes
  useEffect(() => {
    normalizeAccessLocally();
  }, [normalizeAccessLocally, emp?.employee?.user_id, emp?.employeeTeams]);

  return null;
};
